import apiClient from './api';

export interface TopReviewedSegment {
  segmentId: number;
  projectId: number;
  episodeId: number; // 0 for YouTube rows
  contentType: 'podcast' | 'youtube';
  videoId: string; // youtube_videos UUID; '' for podcast rows
  playCount: number;
  vocabCount: number;
  segmentText: string;
  episodeTitle: string; // episode name or video title
  language: string;
  lastPlayedAt: string | null;
}

export interface VocabularyDensity {
  contentId: number; // episode/segment id; 0 for video rows
  contentType: 'segment' | 'episode' | 'video';
  videoId?: string; // youtube_videos UUID; absent for non-video rows
  language: string;
  vocabCount: number;
  title?: string;
}

// Builds the in-app route for a top-reviewed segment: podcast segments open the
// audio player, YouTube segments open the video player. Both deep-link the segment.
export function getSegmentPlayerPath(segment: TopReviewedSegment): string {
  if (segment.contentType === 'youtube') {
    return `/projects/${segment.projectId}/videos/${segment.videoId}?segmentId=${segment.segmentId}`;
  }
  return `/player/${segment.projectId}/${segment.episodeId}?segmentId=${segment.segmentId}`;
}

// Maps the Reports page time-range filter to the API's `days` window.
export function timeRangeToDays(range: '1w' | '1m' | '6m' | '1y'): number {
  switch (range) {
    case '1w':
      return 7;
    case '1m':
      return 30;
    case '6m':
      return 180;
    case '1y':
      return 365;
  }
}

export interface TrackSegmentPlayPayload {
  segmentId: number;
}

export interface GetTopReviewedParams {
  language?: string;
  limit?: number;
  days?: number; // >0 = window in days; omitted/0 = all time
}

export interface GetVocabularyDensityParams {
  language?: string;
  episodeId?: number;
  limit?: number;
  days?: number; // >0 = window in days; omitted/0 = all time
}

export const learningMetricsService = {
  // Track segment play count
  async trackSegmentPlay(payload: TrackSegmentPlayPayload): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(
      '/metrics/segment/play',
      payload
    );
    return response.data;
  },

  // Get top reviewed segments
  async getTopReviewedSegments(params?: GetTopReviewedParams): Promise<TopReviewedSegment[]> {
    const response = await apiClient.get<TopReviewedSegment[]>(
      '/metrics/segments/top-reviewed',
      { params }
    );
    return response.data;
  },

  // Get vocabulary density by episode or segment
  async getVocabularyDensity(params?: GetVocabularyDensityParams): Promise<VocabularyDensity[]> {
    const response = await apiClient.get<VocabularyDensity[]>(
      '/metrics/vocabulary-density',
      { params }
    );
    return response.data;
  },
};
